import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React, { memo, ReactNode } from 'react';
import { IProofLocationProps } from '../../services/hooks/ProofLocation/useGetProofLocation';

interface IOwnProps {
  data?: IProofLocationProps;
  portrait_logo?: string;
  instituteLogo?: string;
}

const ProofLocation: React.FC<IOwnProps> = ({ data, portrait_logo, instituteLogo }: IOwnProps) => {
  const formatStringStyle = (style: string) => {
    const stringSplitted = style.split(';').slice(0, -1);
    let styleObject = {};

    stringSplitted.map((str) => {
      const pointSplit = str.split(':');
      const labelStyle = pointSplit[0].split('-');
      let formatLabelStyle: string;
      if (labelStyle.length > 1) {
        formatLabelStyle = `${labelStyle[0]}${labelStyle[1].charAt(0).toUpperCase()}${labelStyle[1]?.slice(1)} `;
      } else {
        formatLabelStyle = labelStyle[0];
      }
      styleObject = { ...styleObject, [formatLabelStyle.trim()]: pointSplit[1].trim() };
    });

    return styleObject;
  };

  // function subDay(dataStr: string) {
  //   const [dia, mes, ano] = dataStr.split('/').map(Number);
  //   const data = new Date(ano, mes - 1, dia);
  //   data.setDate(data.getDate() - 1);
  //   const novoDia = String(data.getDate()).padStart(2, '0');
  //   const novoMes = String(data.getMonth() + 1).padStart(2, '0');
  //   const novoAno = data.getFullYear();
  //   return `${novoDia}/${novoMes}/${novoAno}`;
  // }

  const removeHtmlTags = (html: string): ReactNode[] => {
    const parseToHtml = new DOMParser();
    const document = parseToHtml.parseFromString(html, 'text/html');
    function convert(node: ChildNode): ReactNode | string {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent;
      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();
      const children = Array.from(node.childNodes).map((child) => convert(child));
      const style = element.getAttribute('style') || '';
      const finalStyle = formatStringStyle(style);
      switch (tag) {
        case 'strong':
          return <Text style={styles.bold}>{children}</Text>;
        case '&nbsp':
          return ' ';
        case 'span': {
          return <Text style={finalStyle}>{children}</Text>;
        }
        case 's':
          return <Text style={{ ...finalStyle, textDecoration: 'line-through' }}>{children}</Text>;
        case 'p': {
          return <Text style={finalStyle}>{children}</Text>;
        }
        case 'em':
          return <Text style={{ ...finalStyle, fontStyle: 'italic' }}>{children}</Text>;
        case 'u': {
          return <Text style={{ ...finalStyle, textDecoration: 'underline' }}>{children}</Text>;
        }
        case 'br':
          break;
        default:
          return <></>;
      }
    }

    const body = document.body;

    return Array.from(body.childNodes).map((child) => convert(child));

    // return html.replace(/<[^>]+>/g, '');
  };

  const styles = StyleSheet.create({
    page: {
      fontFamily: 'Roboto',
      backgroundColor: 'white',
      padding: '50px',
      paddingTop: '30px',
      fontSize: '9px',
    },
    imageHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: '30px',
    },
    header: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      maxWidth: '340px',

      fontWeight: 'bold',
    },
    logo: {
      width: '50px',
    },
    candidateData: {
      marginTop: '20px',
      gap: '7px',
    },
    label: {
      fontWeight: 'bold',
      textAlign: 'right',
      width: '130px',
    },
    content: {
      display: 'flex',
      flexDirection: 'row',
      gap: '3px',
    },
    bold: {
      fontWeight: 'bold',
    },
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.imageHeader}>
          <Image src={portrait_logo} style={styles.logo} />

          <View style={styles.header}>
            <Text>CARTÃO DE CONVOCAÇÃO DE PROVA</Text>
            {data?.step_title && <Text>{data?.step_title?.toUpperCase()}</Text>}

            <Text>{data?.notice_name}</Text>
          </View>
          <Image src={instituteLogo} style={styles.logo} />
        </View>

        <View>
          <View style={styles.candidateData}>
            <Text style={{ textAlign: 'center', marginBottom: '2px', fontWeight: 'bold' }}>
              DADOS DO(A) CANDIDATO(A)
            </Text>
            <View style={styles.content}>
              <Text style={styles.label}>INSCRIÇÃO: </Text>
              <Text>{data?.registration_code}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>{data?.is_cultural_house ? 'TURMA:' : 'VAGA:'}</Text>
              <Text>{data?.first_vacancy.toUpperCase()}</Text>
            </View>
            {data?.second_vacancy && (
              <View style={styles.content}>
                <Text style={styles.label}>TURMA - SEGUNDA OPÇÃO:</Text>
                <Text>{data?.second_vacancy}</Text>
              </View>
            )}

            <View style={styles.content}>
              <Text style={styles.label}>NOME COMPLETO: </Text>
              <Text>{data?.candidate.candidate_name.toUpperCase()}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>CPF: </Text>
              <Text>{data?.candidate.cpf}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>DOCUMENTO: </Text>
              <Text>
                {data?.candidate.document_type}: {data?.candidate.document_number}
              </Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>DATA DE NASCIMENTO: </Text>
              <Text>{data?.candidate.date_of_birth}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>ENDEREÇO: </Text>
              <Text>{data?.candidate.candidate_address.toUpperCase()}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>TELEFONE CELULAR: </Text>
              <Text>{data?.candidate.cellphone}</Text>
            </View>
          </View>

          <View style={styles.candidateData}>
            <Text style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '2px' }}>
              DADOS PARA REALIZAÇÃO DA PROVA
            </Text>
            <View style={styles.content}>
              <Text style={styles.label}>LOCAL DE PROVA: </Text>
              <Text>{data?.candidate_allocation.test_site_name}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>SALA: </Text>
              <Text>
                {data?.exam_schedule.exam_schedule_room_name || data?.candidate_allocation.test_room_name}{' '}
                {data?.candidate_allocation.details && (
                  <Text style={{ fontSize: '7px' }}>({data?.candidate_allocation.details})</Text>
                )}
              </Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>ENDEREÇO: </Text>
              <Text>{data?.candidate_allocation.test_site_address.toUpperCase()}</Text>
            </View>
            <View style={styles.content}>
              <Text style={styles.label}>DATA DA PROVA: </Text>
              <Text>{data?.exam_schedule.exam_schedule_date || data?.candidate_allocation.test_date}</Text>
            </View>
            {(data?.has_exam_schedule && (
              <>
                <View style={styles.content}>
                  <Text style={styles.label}>INICIO DA PROVA: </Text>
                  <Text>{data?.exam_schedule.exam_schedule_start_time}h</Text>
                </View>
                {/* <View style={styles.content}>
                  <Text style={styles.label}>DATA E HORA DO SORTEIO DO PONTO: </Text>
                  <Text>{`${data?.exam_schedule.exam_schedule_date ? subDay(data?.exam_schedule.exam_schedule_date) : '--'} ${data?.exam_schedule.exam_schedule_start_time || '--'}H`}</Text>
                </View> */}
              </>
            )) || (
              <>
                <View style={styles.content}>
                  <Text style={styles.label}>HORA DA PROVA: </Text>
                  <Text>{data?.candidate_allocation.test_hour}h</Text>
                </View>
                <View style={styles.content}>
                  <Text style={styles.label}>ABERTURA DOS PORTÕES:</Text>
                  <Text>{data?.candidate_allocation.gate_opening_time}h</Text>
                </View>
                <View style={styles.content}>
                  <Text style={styles.label}>FECHAMENTO DOS PORTÕES: </Text>
                  <Text>{data?.candidate_allocation.gate_closing_time}h</Text>
                </View>
              </>
            )}
          </View>
          {data?.candidate_registration_card && (
            <View style={styles.candidateData}>
              <Text style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '2px' }}>
                INFORMAÇÕES IMPORTANTES
              </Text>
              <Text>{removeHtmlTags(data?.candidate_registration_card || '')}</Text>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default memo(ProofLocation);
